/**
 * LegalAccountDeletionPage — エールチャット アカウント削除のご案内（公開・未ログイン可）
 *
 * 利用者向け本文に API パス、DELETE、DB status、cancel_at_period_end、tenant、
 * tombstone、Durable Object 等は出さない。実装対応は次のとおり。
 * - UI: web `/settings/profile` の AccountDeletionPanel。mobile は設定の「アカウントを削除」
 * - API: GET /v1/me/account/deletion、DELETE /v1/me/account
 *   {confirmation:'DELETE', close_workspace_ids}
 * - 再認証: requireRecentAuthentication。auth_time / iat から 300 秒
 * - 停止条件: LAST_OWNER。ACTIVE_SUBSCRIPTION（subscriptions.status が
 *   active|trialing|past_due|unpaid|incomplete|paused かつ cancel_at_period_end=0、
 *   または billing_checkouts 残）。自分のみの組織は個別閉鎖同意
 * - 本人: プロフィール・認証の削除/匿名化（users は FK 用 tombstone）
 * - 残るもの: retainedDataNote（共有メッセージ・組織ファイル・タスク・通話・監査）
 * - 後続掃除: TenantDO メンバー除去、R2 の個人アバター/未消費 upload（account_deletion_jobs）
 * - UGC: 通報・対応記録は組織記録。reporter の profile metadata 匿名化は未実装のため本文で断定しない
 * 根拠: api/src/services/account-deletion.ts、docs/retention-account-runtime-qa.md
 * ルート: `/legal/account-deletion`
 */
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export function LegalAccountDeletionPage(): JSX.Element {
  return (
    <div className="min-h-screen bg-surface-base">
      <main
        id="legal-main"
        className="mx-auto w-full max-w-3xl px-4 py-10 sm:px-6 sm:py-16"
        aria-labelledby="legal-account-deletion-heading"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-brand-700">
          Legal / アカウント削除
        </p>
        <h1
          id="legal-account-deletion-heading"
          className="mb-2 text-3xl font-bold leading-tight text-fg-primary"
        >
          アカウント削除のご案内
        </h1>
        <p className="mb-8 text-sm text-fg-muted">
          エールチャット (Aile Chat)　最終改定日: 2026年9月15日
        </p>

        <section className="mb-8 rounded-md border border-border-subtle bg-canvas p-5">
          <h2 className="mb-3 text-xl font-bold text-fg-primary">
            アカウント削除の操作画面
          </h2>
          <p>
            <Link
              to="/settings/profile"
              className="inline-flex items-center rounded-md border border-brand-700 bg-canvas px-4 py-2 text-base font-semibold text-brand-700 hover:underline focus-visible:shadow-focus"
            >
              アカウント削除の操作画面を開く
            </Link>
          </p>
        </section>

        <article className="space-y-8 text-base leading-relaxed text-fg-primary">
          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">手順</h2>
            <ol className="list-decimal space-y-1.5 pl-6 text-fg-secondary">
              <li>ログインします。</li>
              <li>
                Web / デスクトップは
                {" "}
                <Link to="/settings/profile" className="text-brand-700 hover:underline">
                  設定のプロフィール
                </Link>
                、モバイルアプリは設定を開きます。
              </li>
              <li>アカウントの削除条件を確認します。</li>
              <li>他のメンバーがいる組織の最後の所有者なら、所有権を移します。</li>
              <li>自分だけの組織なら、その組織を閉鎖する確認が必要です。継続中の契約等は画面の案内に従い先に処理します。</li>
              <li>確認欄に DELETE と入力して削除します。</li>
            </ol>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">再認証</h2>
            <p className="text-fg-secondary">
              削除を確定するには、直近 5 分以内の再認証が必要です。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">削除される情報</h2>
            <p className="text-fg-secondary">
              プロフィールと認証情報は削除または匿名化されます。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">残る情報</h2>
            <p className="text-fg-secondary">
              組織の共有メッセージ、ファイル、タスク、通話、監査の記録は、保存の定めに従って残ります。通報や対応の記録も組織の記録として残ることがあります。
              プライバシーポリシー第6条のとおり、個人プランの履歴・添付・検索索引は 90 日、法人 Standard にはプランによる履歴期限はありません。組織やルームの個別の保持設定と、利用者の削除が適用されます。利用ログは原則として13ヶ月、課金情報は法定保存期間（税法上は7年）です。くわしくは
              {" "}
              <Link to="/legal/privacy" className="text-brand-700 hover:underline">
                プライバシーポリシー第6条（保存期間）
              </Link>
              をご覧ください。
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-xl font-bold text-fg-primary">
              アカウントを残したままの操作とお問い合わせ
            </h2>
            <p className="mb-3 text-fg-secondary">
              アカウントを残したまま、ルームのメッセージやファイル画面から、自分の投稿やファイルを削除できます。
            </p>
            <p className="text-fg-secondary">
              保有個人データの開示・訂正・削除などの請求は、
              {" "}
              <Link to="/legal/privacy" className="text-brand-700 hover:underline">
                プライバシーポリシー第8条（開示請求等の窓口）および第10条（お問い合わせ先）
              </Link>
              の個人情報保護管理者（support@skillbridge.co.jp）へお願いします。
            </p>
          </section>
        </article>

        <footer className="mt-12 border-t border-border-subtle pt-6">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 rounded px-1 py-0.5 text-sm text-brand-700 hover:underline focus-visible:shadow-focus"
          >
            <ArrowLeft size={14} aria-hidden /> ログイン画面に戻る
          </Link>
        </footer>
      </main>
    </div>
  );
}
